import type { Signal } from '@angular/core'
import { computed, Injectable, inject } from '@angular/core'
import { EntitySignalQuery } from '@awork/core/state/signal-store/entitySignalQuery'
import type { FilterEntity } from '@awork/_shared/models/filters.model'
import type { FilterEntityType } from '@awork/_shared/models/filters.types'
import { EntityFilterStore } from '@awork/_shared/state/entity-filter.store'
import { UserQuery } from '../../features/user/state/user.query'

@Injectable({ providedIn: 'root' })
export class EntityFilterQuery extends EntitySignalQuery<FilterEntity> {
  protected store: EntityFilterStore

  private userQuery = inject(UserQuery)
  private currentUser = this.userQuery.queryCurrentUser()

  constructor() {
    const store = inject(EntityFilterStore)

    super(store)

    this.store = store
  }

  /**
   * Gets a single entity filter from the store
   * @param {string} filterId - Filter ID
   * @returns {Signal<FilterEntity>}
   */
  queryEntityFilter(filterId: string): Signal<FilterEntity> {
    return this.queryEntity(filterId)
  }

  /**
   * Query entity filters by type with optional shared filtering
   * @param {FilterEntityType} entityType - Entity type to filter by
   * @param {boolean} isShared - Optional flag to query shared filters
   * @deprecated The isShared parameter is deprecated. TODO: Remove when granular sharing is fully active and isShared is removed from FilterEntity.
   * @returns {Signal<FilterEntity[]>}
   */
  queryEntityFilters(entityType: FilterEntityType, isShared?: boolean): Signal<FilterEntity[]> {
    return computed(() => {
      const currentUserId = this.currentUser()?.id
      const filters = this.queryAll({
        filterBy: filter => {
          const typeMatch = filter.entityType === entityType
          const sharedMatch = isShared !== undefined ? (filter.userId !== currentUserId) === isShared : true

          return typeMatch && sharedMatch
        },
        sortBy: (a, b) => {
          return a.name.localeCompare(b.name)
        }
      })

      return filters()
    })
  }

  /**
   * Query all entity filters from the store regardless of type
   * @returns {Signal<FilterEntity[]>}
   */
  queryAllEntityFilters(): Signal<FilterEntity[]> {
    return this.queryAll({
      sortBy: (a, b) => {
        return a.name.localeCompare(b.name)
      }
    })
  }
}
